// @flow
import type { Action } from "redux-action";
import {
  ToggleAddEditReportModal,
  ToggleSaveReportModal,
  ToggleScheduleReportSettingsModal,
  GetScheduledReportDataSuccess,
  GetScheduledReportDataError,
  SubmitScheduleReportSettings,
  SetScheduledReportSettingsModalData,
  SubmitScheduleReportSettingsSuccess,
  SubmitScheduleReportSettingsError,
  ToggleCancelTaskModal,
  ToggleMiLogModal,
  ToggleMiShareModal,
} from "@modules/reporting/constants/actions";
import type { AddEditReportModalType, ReportingModalsState } from "./types";
import { cloneObject } from "@utils/object";

export const initialState: ReportingModalsState = {
  isLoading: false,
  historicReportModal: {
    visible: false,
  },
  currentReportModal: {
    visible: false,
  },
  addEditReportModal: {
    visible: false,
    initialStep: 1,
    data: null,
  },
  saveReportModal: {
    visible: false,
    data: null,
    type: null,
    report: null,
  },
  scheduleReportSettingsModal: {
    visible: false,
    data: null,
  },
  cancelTaskModal: {
    visible: false,
    data: null,
  },
  miLogModal: {
    visible: false,
    report: null,
  },
  miShareModal: {
    visible: false,
    report: null,
  },
};

export const onToggleAddEditReportModal = (state: ReportingModalsState) => (
  action: Action<AddEditReportModalType>,
) => {
  const res = {
    ...state,
    addEditReportModal: {
      data: state.addEditReportModal.visible ? null : state.addEditReportModal.data,
      initialStep: state.addEditReportModal.visible ? 1 : state.addEditReportModal.initialStep,
      ...action.payload,
    },
  };
  return res;
};

export const onToggleCancelTaskModal = (state: ReportingModalsState) => ({ payload }: Object) => {
  const data = !state.cancelTaskModal.visible ? payload : null;
  return {
    ...state,
    cancelTaskModal: {
      ...state.cancelTaskModal,
      visible: !state.cancelTaskModal.visible,
      data,
    },
  };
};

export const onToggleScheduleReportSettingsModal = (state: ReportingModalsState) => () => {
  const visible = state.scheduleReportSettingsModal.visible;
  const data = !visible ? state.scheduleReportSettingsModal.data : null;
  return {
    ...state,
    scheduleReportSettingsModal: {
      ...state.scheduleReportSettingsModal,
      data,
      visible: !visible,
    },
  };
};

export const onSetScheduledReportData = (state: Object) => ({ payload }: Object) => {
  return {
    ...state,
    scheduleReportSettingsModal: {
      ...state.scheduleReportSettingsModal,
      data: payload,
    },
  };
};

export const onGetScheduledReportDataError = (state: Object) => ({ payload }: Object) => {
  return {
    ...state,
    scheduleReportSettingsModal: {
      ...state.scheduleReportSettingsModal,
      data: null,
    },
  };
};

export const onSubmitScheduleReportSettings = (state: Object) => ({ payload }: Object) => {
  return {
    ...state,
    isLoading: ++state.isLoading,
  };
};

export const onSubmitScheduleReportSettingsSuccess = (state: Object) => ({ payload }: Object) => {
  return {
    ...state,
    isLoading: --state.isLoading,
    scheduleReportSettingsModal: {
      visible: false,
    },
  };
};
export const onSubmitScheduleReportSettingsError = (state: Object) => ({ payload }: Object) => {
  return {
    ...state,
    isLoading: --state.isLoading,
  };
};

export const onToggleSaveReportModal = (state: ReportingModalsState) => ({ payload }: Object) => {
  if (payload.visible === false) {
    return {
      ...state,
      saveReportModal: {
        visible: false,
        data: null,
        type: null,
        report: null,
      },
    };
  } else {
    return {
      ...state,
      saveReportModal: {
        ...state.saveReportModal,
        ...payload,
      },
    };
  }
};

export const onToggleMiLogModal = (state: ReportingModalsState) => ({ payload }: Object) => ({
  ...state,
  miLogModal: {
    ...state.miLogModal,
    ...payload,
    visible: !state.miLogModal.visible,
  },
});

export const onToggleMiShareModal = (state: ReportingModalsState) => ({ payload }: Object) => ({
  ...state,
  miShareModal: {
    report: state.miShareModal.visible ? null : payload.report,
    visible: !state.miShareModal.visible,
  },
});

export default (state: ReportingModalsState = initialState, action: *): ReportingModalsState => {
  const act = {
    [SubmitScheduleReportSettings]: onSubmitScheduleReportSettings,
    [SubmitScheduleReportSettingsSuccess]: onSubmitScheduleReportSettingsSuccess,
    [SubmitScheduleReportSettingsError]: onSubmitScheduleReportSettingsError,
    [GetScheduledReportDataError]: onGetScheduledReportDataError,
    [GetScheduledReportDataSuccess]: onSetScheduledReportData,
    [SetScheduledReportSettingsModalData]: onSetScheduledReportData,
    [ToggleAddEditReportModal]: onToggleAddEditReportModal,
    [ToggleScheduleReportSettingsModal]: onToggleScheduleReportSettingsModal,
    [ToggleSaveReportModal]: onToggleSaveReportModal,
    [ToggleCancelTaskModal]: onToggleCancelTaskModal,
    [ToggleMiLogModal]: onToggleMiLogModal,
    [ToggleMiShareModal]: onToggleMiShareModal,
  };

  if (action && action.type && act && act[action.type]) {
    return act[action.type](cloneObject(state))(action);
  }
  return state;
};
